import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import { type ProviderData, useAuth } from 'components/Auth';
import { renderEventMatchingIcon } from 'components/Calendaring';
import {
  type CIReferral,
  CIReferralStatus
} from 'components/CriticalIncident/interfaces';
import { Spinner, TruncatedText } from 'components/Shared';
import { type PaginationDetails, Paginator } from 'components/Shared/MaterialTable';
import useTimezoneUtils from 'hooks/useTimezoneUtils';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CIReferralDecisionModal } from '../../CIReferralDecisionModal';
import { PriorityCell } from '../CIReferralsTable/CIReferralsTableCells/PriorityCell';
import { StatusCell } from '../CIReferralsTable/CIReferralsTableCells/StatusCell';
import { PriorityAlert } from 'components/Shared/PriorityAlert';
import { toShortTimezone } from 'utils';
import { formatAddress } from 'components/CriticalIncident/utils/formatters';

import './MobileCIReferralsList.scss';

export const MobileCIReferralsList: React.FC<{
    referrals: CIReferral[] | undefined;
    dataLoading: boolean;
    paginationDetails: PaginationDetails;
    handlePageChange: (pageNumber: number) => void;
    emptyStateMessage: string;
}> = ({ referrals, dataLoading, paginationDetails, handlePageChange, emptyStateMessage }) => {
    const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
    const { effectiveProviderData = {} as ProviderData } = useAuth();

    const [selectedReferralId, setSelectedReferralId] = useState<string | null>(null);

    const handleClose = () => {
        setSelectedReferralId(null);
    };

    const { localeTimeFormat, formatDate } = useTimezoneUtils();

    const getbuttonLabel = (referral: CIReferral) =>
        referral.status === CIReferralStatus.Open ? t('respond') : t('view');

    const MobileCIReferralsListSkeleton: React.FC = () => {
        return (
            <List className="ci_referrals_list">
                <ListItem>
                    <ListItemAvatar className="ci_referrals_list_item">
                        <Skeleton variant="circular" width={40} height={40} />
                    </ListItemAvatar>

                    <ListItemText>
                        <Skeleton variant="text" width="90%" height={24} />
                        <Skeleton variant="text" width="50%" height={20} />
                    </ListItemText>
                </ListItem>
                <Skeleton variant="rectangular" height={1} />
                <ListItem>
                    <Skeleton variant="rounded" width={58} height={26} />
                    <Skeleton variant="rounded" width={58} height={26} />
                    <Skeleton variant="rounded" width={58} height={26} />
                </ListItem>
                <Skeleton variant="rectangular" height={1} width="100%" />
            </List>
        );
    };

    return (
        <React.Fragment>
            {translationsReady ? (
                <React.Fragment>

                    {dataLoading ? (
                        Array.from({ length: referrals?.length || 10 }, (_, index) => (
                            <MobileCIReferralsListSkeleton key={index} />
                        ))
                    ) : referrals && referrals.length === 0 ? (
                        <div className="referrals_no_entries">
                            <p>{emptyStateMessage}</p>
                        </div>
                    ) : (
                        <React.Fragment>
                            <PriorityAlert simple priority="normal" iconTooltip={t('time_zone_hint')}>
                                {t('referrals_are_shown_in_your_timezone', { timezone: toShortTimezone(effectiveProviderData?.timeZone ?? '') })}
                            </PriorityAlert>
                            <List className="ci_referrals_list" sx={{ paddingTop: 0 }}>
                                {referrals?.map((referral) => {
                                    const bookingTime = formatDate(
                                        moment(referral.bookingStartTime),
                                        `MMM Do, ${localeTimeFormat}`
                                    );

                                    return (
                                        <React.Fragment key={referral.referralId}>
                                            <ListItem>
                                                <ListItemAvatar className="ci_referrals_list_item">
                                                    <Avatar>{renderEventMatchingIcon(referral.modality.trim())}</Avatar>
                                                </ListItemAvatar>

                                                <ListItemText
                                                    primary={referral.companyName}
                                                    secondary={<ListItemDescription referral={referral} bookingTime={bookingTime} />}
                                                />

                                                <Button
                                                    variant="text"
                                                    onClick={() => setSelectedReferralId(referral.referralId)}
                                                >
                                                    {getbuttonLabel(referral)}
                                                </Button>
                                                {selectedReferralId === referral.referralId && (
                                                    <CIReferralDecisionModal
                                                        open={true}
                                                        onClose={handleClose}
                                                        providerId={effectiveProviderData!.providerId}
                                                        referralId={referral.referralId}
                                                    />
                                                )}
                                            </ListItem>
                                            <Divider variant="middle" component="li" />
                                            <ListItem sx={{ padding: '16px' }}>
                                                <StatusCell status={referral.status} />
                                                <PriorityCell priority={referral.priority} />
                                            </ListItem>
                                            <Divider variant="fullWidth" component="li" />
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                            {paginationDetails && (
                                <Paginator paginationDetails={paginationDetails} onPageChange={handlePageChange} isInfiniteScroll />
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : (
                <Spinner fillParent />
            )}
        </React.Fragment>
    );
};

const ListItemDescription: React.FC<{ referral: CIReferral; bookingTime: string; }> = ({ referral, bookingTime }) => {
    const { t } = useTranslation('criticalIncident', { useSuspense: false });
    const { city, state } = { ...referral?.location };

    const location = formatAddress(referral?.location, { cityAndStateOnly: true, t }); 
    return ['inperson', 'in person'].includes(referral.modality.trim().toLowerCase()) && (!!city || !!state)
            ? <span className="ci_referrals_list_item_description"><span>{referral.externalWorkOrderId}</span><br /><TruncatedText>{location}</TruncatedText><br /><span>{bookingTime}</span></span>
            : <span className="ci_referrals_list_item_description"><span>{referral.externalWorkOrderId}</span><br /><span>{bookingTime}</span></span>;
}